/**
 * <anchor-toc data-content="#selector"> — in-page anchor navigation for the
 * shizuku long-form page template (sections/long-form-page.liquid).
 *
 * Builds a link list from the h2 headings found in the referenced content
 * container and highlights the section currently in view. Migrated content
 * carries stable explicit ids (see the anchor-slug contract in the section
 * header); h2s WITHOUT an id (e.g. new sections typed by the merchant in
 * the admin page editor — no HTML editing needed) get one auto-generated
 * from their text with the same slug rules, and a pending #fragment
 * pointing at an auto id is scrolled to once it exists.
 *
 * Legacy-fragment fallback (P15C session 3, task A3): the English bodies used
 * to carry the GERMAN anchor ids. They now carry English ones and keep the
 * old id as data-de-anchor, so an old link like
 * /en/pages/shizuku-store#oeffnungszeiten still lands on «Opening hours».
 * This has to happen client-side — browsers never send the fragment, so no
 * redirect can catch it. Same idea as the data-craft-id remapper in
 * sections/brewery-overview.liquid.
 *
 * The highlight is position-based (rAF-throttled scroll listener) rather
 * than IntersectionObserver-based: the active heading is the last one above
 * the activation line, clicking a link activates it directly, and at the
 * very bottom of the page the last heading wins — this stays correct for
 * short sections that can never reach the top of the viewport. Horizon
 * scrolls `.page-wrapper` on desktop and the document on mobile, so both
 * are listened to.
 */
const ACTIVATION_OFFSET = 130; // px below viewport top; > the 6rem scroll-margin
const CLICK_SUPPRESS_MS = 1000; // let smooth scrolling settle after a click

/**
 * Same slug rules as the P13 anchor contract: lowercase-kebab,
 * ä→ae ö→oe ü→ue ß→ss, everything else non-alphanumeric collapses to «-».
 * @param {string} text
 */
function slugify(text) {
  return text
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

class AnchorTocComponent extends HTMLElement {
  /** @type {HTMLElement[]} */
  #headings = [];
  #suppressUntil = 0;
  #rafPending = false;
  #onScroll = () => this.#scheduleUpdate();

  connectedCallback() {
    const selector = this.getAttribute('data-content');
    const content = selector ? document.querySelector(selector) : null;
    this.#headings = content ? Array.from(content.querySelectorAll('h2')) : [];

    let assignedPendingFragment = false;
    for (const heading of this.#headings) {
      if (heading.id) continue;
      const slug = slugify(heading.textContent ?? '');
      if (!slug || document.getElementById(slug)) continue;
      heading.id = slug;
      if (`#${slug}` === window.location.hash) assignedPendingFragment = true;
    }
    this.#headings = this.#headings.filter((h) => h.id);

    if (this.#headings.length === 0) {
      this.hidden = true;
      return;
    }

    // the browser couldn't resolve the fragment before the id existed
    if (assignedPendingFragment) {
      document.querySelector(window.location.hash)?.scrollIntoView();
    }

    this.#resolveLegacyFragment(content);
    // …and again once everything has loaded: the browser restores the
    // previous scroll position of a revisited page AFTER this callback,
    // which would otherwise win over our jump.
    if (document.readyState !== 'complete') {
      window.addEventListener(
        'load',
        () => this.#resolveLegacyFragment(content),
        { once: true }
      );
    }

    // legacy cms-block titles are self-anchor permalinks (<h3><a href="#id">):
    // wrap each heading's content in a link to its own id (P15B task 10) —
    // skip headings that already carry an authored link
    for (const heading of this.#headings) {
      if (heading.querySelector('a')) continue;
      const selfLink = document.createElement('a');
      selfLink.href = `#${heading.id}`;
      selfLink.append(...heading.childNodes);
      heading.appendChild(selfLink);
    }

    const list = document.createElement('ul');
    list.className = 'anchor-toc__list';
    list.setAttribute('role', 'list');

    for (const heading of this.#headings) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'anchor-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent?.trim() ?? '';
      item.appendChild(link);
      list.appendChild(item);
    }

    this.replaceChildren(list);

    list.addEventListener('click', (event) => {
      if (!(event.target instanceof HTMLAnchorElement)) return;
      // activate the clicked target directly and let the smooth scroll
      // settle before the position spy takes over again
      this.#setActive(event.target.hash.slice(1));
      this.#suppressUntil = Date.now() + CLICK_SUPPRESS_MS;
      // on mobile the TOC sits inside a collapsed <details>; close it again
      // after picking an anchor so it doesn't cover the jump target
      const details = this.closest('details');
      if (details && !window.matchMedia('(min-width: 990px)').matches) {
        details.open = false;
      }
    });

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    document.querySelector('.page-wrapper')
      ?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    document.querySelector('.page-wrapper')
      ?.removeEventListener('scroll', this.#onScroll);
  }

  /**
   * A German fragment from before the English anchors existed: find the
   * heading that still records it and scroll there. Only runs when the
   * fragment matches nothing — a live id always wins.
   * @param {Element | null} content
   */
  #resolveLegacyFragment(content) {
    const hash = window.location.hash.slice(1);
    if (!hash || !content) return;
    let decoded = hash;
    try {
      decoded = decodeURIComponent(hash);
    } catch {
      /* malformed escape — use the raw fragment */
    }
    if (document.getElementById(decoded) || document.getElementById(hash)) {
      return;
    }
    const target = content.querySelector(
      `[data-de-anchor="${CSS.escape(decoded)}"]`
    );
    if (!target) return;
    target.scrollIntoView();
    this.#setActive(target.id);
    this.#suppressUntil = Date.now() + CLICK_SUPPRESS_MS;
  }

  #scheduleUpdate() {
    if (this.#rafPending) return;
    this.#rafPending = true;
    requestAnimationFrame(() => {
      this.#rafPending = false;
      this.#updateFromPosition();
    });
  }

  #updateFromPosition() {
    if (Date.now() < this.#suppressUntil) return;
    if (this.#headings.length === 0) return;

    const scroller = document.querySelector('.page-wrapper');
    const atBottom = scroller
      ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
      : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    let current = this.#headings[0];
    if (atBottom) {
      current = this.#headings[this.#headings.length - 1];
    } else {
      for (const heading of this.#headings) {
        if (heading.getBoundingClientRect().top <= ACTIVATION_OFFSET) {
          current = heading;
        }
      }
    }
    this.#setActive(current.id);
  }

  /** @param {string} id */
  #setActive(id) {
    for (const link of this.querySelectorAll('.anchor-toc__link')) {
      const isActive = link.getAttribute('href') === `#${id}`;
      link.classList.toggle('anchor-toc__link--active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('anchor-toc')) {
  customElements.define('anchor-toc', AnchorTocComponent);
}
